<script setup>
import { ref, watch, onBeforeUnmount } from 'vue';

const props = defineProps({
  isDragging: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['start-drag', 'reset-width']);

// Hover delay: only reveal the handle after ~800ms of hovering
const isHovering = ref(false);
const isRevealed = ref(false);
let hoverTimer = null;

function onMouseEnter() {
  isHovering.value = true;
  hoverTimer = setTimeout(() => {
    if (isHovering.value) {
      isRevealed.value = true;
    }
  }, 800);
}

function onMouseLeave() {
  isHovering.value = false;
  clearTimeout(hoverTimer);
  // Keep revealed while dragging; it will be reset via the watcher below
  if (!props.isDragging) {
    isRevealed.value = false;
  }
}

function onMouseDown(event) {
  emit('start-drag', event);
}

function onDoubleClick() {
  emit('reset-width');
}

// When dragging ends (mouseup outside handle), reset the revealed state.
// Without this, isRevealed gets stuck true because onMouseLeave already
// fired while isDragging was true and skipped the reset.
watch(
  () => props.isDragging,
  (newVal, oldVal) => {
    if (oldVal && !newVal && !isHovering.value) {
      isRevealed.value = false;
    }
  }
);

onBeforeUnmount(() => {
  clearTimeout(hoverTimer);
});
</script>

<template>
  <div
    class="conversation-resize-handle"
    :class="{
      'is-revealed': isRevealed || isDragging,
      'is-dragging': isDragging,
    }"
    @mouseenter="onMouseEnter"
    @mouseleave="onMouseLeave"
    @mousedown="onMouseDown"
    @dblclick="onDoubleClick"
  >
    <div class="resize-handle-line" />
  </div>
</template>

<style scoped>
.conversation-resize-handle {
  position: relative;
  width: 8px;
  flex-shrink: 0;
  cursor: col-resize;
  z-index: 10;
  display: flex;
  align-items: center;
  justify-content: center;
  /* Transparent hit area, always present */
}

.resize-handle-line {
  width: 2px;
  height: 100%;
  border-radius: 1px;
  background-color: transparent;
  transition: background-color 0.3s ease, width 0.2s ease;
}

/* Revealed state: bar becomes visible after hover delay */
.conversation-resize-handle.is-revealed .resize-handle-line {
  background-color: var(--n-slate-6, #475569);
  width: 3px;
}

/* Active dragging: accent color */
.conversation-resize-handle.is-dragging .resize-handle-line,
.conversation-resize-handle.is-revealed:hover .resize-handle-line {
  background-color: var(--n-brand, #6366f1);
  width: 3px;
}
</style>
